/**
 * The companion's empty-state heading.
 *
 * <h2>Why it varies</h2>
 * A user signs in once and opens the panel many times a day, so a fixed line reads as canned by the third
 * look. This varies it two ways that cost nothing: the time of day, and whether this is the first empty
 * state of the session.
 *
 * <h2>Why `hour` and `visit` are parameters</h2>
 * Nothing here reads the clock or any store, which is what makes the time bands testable without fake
 * timers and keeps the same input producing the same output. The caller supplies both — see the store,
 * which advances `visit` at exactly the two moments the empty state can appear.
 */

/**
 * Cycled by visit, so the tenth open of the day does not read the same as the second.
 *
 * An earlier cut of this only distinguished "first visit" from "any later visit", which meant every visit
 * after the first was word-for-word identical — the exact staleness this is meant to avoid, just moved along
 * by one. Rotating the question against the three time bands gives twelve distinct headings before any
 * repeat, which is more than a session realistically shows.
 */
const QUESTIONS = [
  "what can I help you with today?",
  "what else can I help with?",
  "what would you like to look at?",
  "what can I look up for you?",
] as const;

/**
 * Time-of-day lead for an hour in 24-hour form.
 *
 * The small hours resolve to "Good evening" rather than falling through to a wrong band — someone working
 * at 2am should not be told "Good morning".
 */
function leadForHour(hour: number): string {
  if (hour >= 5 && hour <= 11) return "Good morning";
  if (hour >= 12 && hour <= 16) return "Good afternoon";
  return "Good evening";
}

/**
 * The two halves of the heading, kept apart on purpose.
 *
 * Joined into one sentence they wrapped wherever the panel's narrow column ran out — which left the joining
 * dash stranded at the end of a line and the remainder centred underneath. Returning the parts lets the
 * component put each on its own line, so the break is deliberate at every name length and every rotation.
 */
export interface Greeting {
  /** "Good afternoon, Regoadmin" — the salutation. */
  lead: string;
  /** "What can I help you with today?" — sentence-cased, since it now starts its own line. */
  question: string;
}

function sentenceCase(text: string): string {
  return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
}

/**
 * @param firstName the user's first name, or {@code null} when none could be resolved
 * @param hour      current hour, 0–23
 * @param visit     1 for the first empty state of the session, incrementing for later ones
 */
export function buildGreeting(
  firstName: string | null | undefined,
  hour: number,
  visit: number,
): Greeting {
  // The time-of-day lead is kept on every visit, not just the first: it is what makes the heading differ
  // between a morning and an afternoon open of the same session.
  const lead = leadForHour(hour);
  const index = (Math.max(1, Math.trunc(visit)) - 1) % QUESTIONS.length;

  // Without a name the comma goes too, so the line never renders with a gap or a stray punctuation mark.
  const name = firstName?.trim();
  return {
    lead: name ? `${lead}, ${name}` : lead,
    question: sentenceCase(QUESTIONS[index]),
  };
}

/**
 * Shown if the panel somehow renders its empty state without the store having chosen a greeting. Defensive
 * only — `openPanel` is the sole way in and always sets one — but it means the component never has to
 * compute anything itself, which is what keeps the text stable across re-renders.
 */
export const DEFAULT_GREETING: Greeting = {
  lead: "Hello",
  question: sentenceCase(QUESTIONS[0]),
};

/** Exposed for tests, so they assert against the shipped wording rather than a copy of it. */
export const GREETING_QUESTIONS: readonly string[] = QUESTIONS;
